import * as React from 'react';
import { Schema } from './schema';
import styles from './styles.module.css';
import { t } from '~i18n';
import { IconButton, Panel, Select, Tooltip, renderIcon } from '@ixicare/ui';
import { canRunAction, GatingContext } from './utils/gating';
import { Expand, Minimize } from 'lucide-react';

type FieldRef = { id: string; label: string };

interface Props {
  schema: Schema;
  dirtyItems: FieldRef[];
  siteOverrideItems: FieldRef[];
  differingItems: FieldRef[];
  profile: string;
  headerLabel: string | null;
  gating: GatingContext;
  onProfileChange: (profile: string) => void;
  onExportClick: () => void;
  onRevealField: (id: string) => void;
  collapsedSections: Record<string, boolean>;
  setCollapsedSections: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
}

const PageHeader: React.FC<Props> = ({
  schema,
  headerLabel,
  dirtyItems,
  siteOverrideItems,
  differingItems,
  profile,
  gating,
  onProfileChange,
  onExportClick,
  onRevealField,
  collapsedSections,
  setCollapsedSections,
}) => {
  const isAllCollapsed = React.useMemo(() => {
    if (!collapsedSections) return;
    return Object.values(collapsedSections).every((c) => c);
  }, [collapsedSections]);

  const downloadAction = (schema.actions ?? []).find(
    (a) => a.id === 'download_config' && a.placement === 'header' && canRunAction(a, gating)
  );

  const fieldLinks = (items: FieldRef[]): React.ReactNode => (
    <div className={styles.tooltipLinks}>
      {items.map((it) => (
        <a
          key={it.id}
          href={`#${it.id}`}
          className={styles.tooltipLink}
          onClick={(e) => {
            e.preventDefault();
            onRevealField(it.id);
          }}
        >
          {it.label}
        </a>
      ))}
    </div>
  );

  // Plain span when there is nothing to list
  const summaryChip = (label: string, color: string, items: FieldRef[]) => {
    const text = (
      <>
        ● {items.length} {label}
      </>
    );

    if (!items.length) {
      return (
        <span className={styles.sourceChip} style={{ color }}>
          {text}
        </span>
      );
    }

    return (
      <Tooltip title={label} content={fieldLinks(items)} overrideWidth={300}>
        <u className={styles.sourceChip} style={{ color }}>
          {text}
        </u>
      </Tooltip>
    );
  };

  return (
    <div className="mb-0">
      <Panel.Title className={styles.titleRow}>
        <span className={styles.titleIcon} aria-hidden>
          {renderIcon('cog', undefined)}
        </span>
        <span>{t('device-config.page.title')}</span>
        {headerLabel && <span className={styles.mac}>{headerLabel}</span>}
      </Panel.Title>
      <Panel.Right>
        {downloadAction && (
          <button
            type="button"
            className={styles.ActionButton}
            onClick={onExportClick}
            aria-label={t(downloadAction.label_key)}
            title={t(downloadAction.label_key)}
          >
            {renderIcon(downloadAction.icon ?? 'export', undefined)}
          </button>
        )}
        {schema.meta.profile && (
          <Select
            label={t('device-config.profile.title')}
            value={profile}
            options={schema.meta.profile.options.map((p) => ({
              value: p,
              label: t(`device-config.profile.${p}`),
            }))}
            onChange={(p) => onProfileChange(p.value)}
          />
        )}
      </Panel.Right>
      <Panel.Section className={styles.pageHead}>
        <div>
          <p className={styles.subhead}>{t(schema.meta.subtitle_key)}</p>
          <div className={styles.sourceChips}>
            {summaryChip(t('device-config.unsaved-changes'), '#2a335c', dirtyItems)}
            {summaryChip(t('device-config.site-overrides'), '#5b6fb3', siteOverrideItems)}
            {differingItems.length > 0 && summaryChip(t('device-config.bulk.differing'), '#c2410c', differingItems)}
          </div>
        </div>
        <Tooltip title={t('device-config.expandAll')}>
          <div className={styles.expandAll}>
            <IconButton
              lucide={isAllCollapsed ? Expand : Minimize}
              onClick={() =>
                setCollapsedSections(
                  Object.fromEntries(Object.entries(collapsedSections).map(([id]) => [id, !isAllCollapsed]))
                )
              }
            />
          </div>
        </Tooltip>
      </Panel.Section>
    </div>
  );
};

export default PageHeader;
